// Where Outreach is working: Master (the shared library — roles, research, templates,
// senders, import) or one company (e-Veritas / MailBIT / TwinShield — its contacts
// and logs only).
//
// The choice is kept in localStorage and sent with every Outreach API call as
// X-Outreach-Business (0 = Master), so pages don't thread it through. Company
// endpoints refuse Master, so a company page can never show another company's data.
// Switching reloads the page: a reload is the one refresh that can't leave the old
// company's rows on screen.

import { useEffect, useState } from "react";

export interface Business {
  id: number; key: string; name: string; website: string | null;
  default_from_name: string | null; default_from_email: string | null; default_reply_to: string | null;
}

const LS_KEY = "outreach_business_id";
export const MASTER_ID = 0;
export const DEFAULT_BUSINESS_ID = 1;   // e-Veritas — the only env-configured sending account

export function currentBusinessId(): number {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const n = raw == null ? MASTER_ID : Number(raw);
    return Number.isInteger(n) && n >= 0 ? n : MASTER_ID;
  } catch { return MASTER_ID; }
}

export function isMasterScope(): boolean { return currentBusinessId() === MASTER_ID; }

export function switchBusiness(id: number, path?: string) {
  try { localStorage.setItem(LS_KEY, String(id)); } catch { /* private mode */ }
  if (path) window.location.href = path; else window.location.reload();
}

/** fetch() for /api/outreach/* that names the current scope. */
export function outreachFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set("X-Outreach-Business", String(currentBusinessId()));
  return fetch(input, { credentials: "include", ...init, headers });
}

let cache: Promise<Business[]> | null = null;
export function loadBusinesses(): Promise<Business[]> {
  cache ??= fetch("/api/outreach/businesses", { credentials: "include" })
    .then(r => (r.ok ? r.json() : { businesses: [] }) as Promise<{ businesses?: Business[] }>)
    .then(d => d.businesses || [])
    .catch(() => []);
  return cache;
}

export function useBusiness() {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  useEffect(() => { loadBusinesses().then(setBusinesses); }, []);
  const id = currentBusinessId();
  const isMaster = id === MASTER_ID;
  const current = isMaster ? null : businesses.find(b => b.id === id) || null;
  return { businesses, current, id, isMaster };
}
